import { SkyBadge, SkyDialog, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import { dbtNodeRows } from "@shared/types/dbtCauses";
import type { TransformationGraph, TransformationGraphNode } from "@shared/types/transformations";
import { CauseBlock, CauseNotClassified } from "./CauseBlock";
import { NODE_HEIGHT, NODE_WIDTH, ORIGIN_Y, layoutGraph } from "./graphLayout";
import {
  formatRows,
  formatSeconds,
  nodeStatusPresentation,
  testCountsFor,
  type Presentation,
  type TestCounts,
  NO_TEST_COUNTS,
  testCountsLine,
  materializationLabel,
} from "./transformFormatting";

/**
 * The model DAG of the last run (canvas 16 block 01; FR-62, SEC-17, NFR-7).
 *
 * Inline SVG: edges are paths, nodes are groups. Each node is a focusable
 * `role="button"` with `aria-haspopup="dialog"` and a label that reads name,
 * kind and status; Enter or Space opens the node's facts in a dialog, and the
 * arrow keys walk the nodes in depth order. Not `role="img"`: a focusable,
 * Enter-activated image is an axe finding, and the graph is also not the only
 * way to read a status — the Tests and Runs tabs carry the same facts as tables.
 *
 * Every string on a node is an identifier, a count or a timing: unique id,
 * kind, materialization, rows, status, test counts. No compiled SQL, no dbt
 * message, no failing value ever reaches this component, because the API never
 * sends one (SEC-17).
 *
 * The container scrolls sideways on its own; the page never does.
 */

const PILL_HEIGHT = 18;

/**
 * A pill's width for its label at 12 px semibold, the app's badge text (round 7 T-37: the graph's
 * text left its own 10.5 to 12.5 px sizes for the type scale; 7.1 px a character, as 6.2 was at
 * 10.5 px).
 */
function pillWidth(label: string): number {
  return Math.round(label.length * 7.1 + 14);
}

const PILL_CLASSES: Record<Presentation["color"], { rect: string; text: string }> = {
  success: {
    rect: "fill-success-subtle stroke-success-border",
    text: "fill-success-subtle-foreground",
  },
  danger: {
    rect: "fill-destructive-subtle stroke-destructive-border",
    text: "fill-destructive-subtle-foreground",
  },
  warning: {
    rect: "fill-warning-subtle stroke-warning-border",
    text: "fill-warning-subtle-foreground",
  },
  informative: { rect: "fill-info-subtle stroke-info-border", text: "fill-info-subtle-foreground" },
  subtle: { rect: "fill-secondary stroke-border", text: "fill-muted-foreground" },
  brand: { rect: "fill-secondary stroke-border", text: "fill-muted-foreground" },
  important: { rect: "fill-secondary stroke-border", text: "fill-muted-foreground" },
  severe: { rect: "fill-secondary stroke-border", text: "fill-muted-foreground" },
};

function Pill({
  x,
  y,
  label,
  color,
}: {
  x: number;
  y: number;
  label: string;
  color: Presentation["color"];
}) {
  const width = pillWidth(label);
  const classes = PILL_CLASSES[color];
  return (
    <g transform={`translate(${x},${y})`}>
      <rect width={width} height={PILL_HEIGHT} rx={9} className={classes.rect} strokeWidth={1} />
      <text
        x={width / 2}
        y={13}
        textAnchor="middle"
        className={`${classes.text} text-xs font-semibold`}
      >
        {label}
      </text>
    </g>
  );
}

/**
 * The rows a node wrote: none for a view or an ephemeral node (#1093, `dbtNodeRows`), whatever
 * count its outcome carries.
 */
function nodeRows(node: TransformationGraphNode): number | null {
  return dbtNodeRows(node.materialized, node.rowsAffected);
}

/** The middle line of a node: rows for a table, the materialization otherwise. */
function nodeMeasure(node: TransformationGraphNode): string {
  if (node.resourceType === "source") return "source";
  const rows = nodeRows(node);
  if (typeof rows === "number") return formatRows(rows);
  return node.materialized ?? node.resourceType;
}

function nodeAriaLabel(
  node: TransformationGraphNode,
  tests: TestCounts,
  highlighted: boolean,
): string {
  const parts = [
    node.name,
    node.resourceType === "source" ? "source" : (node.materialized ?? "model"),
  ];
  const status = nodeStatusPresentation(node.status);
  if (status) parts.push(status.label);
  if (tests.failed > 0) parts.push(`${tests.failed} tests failed`);
  else if (tests.ok > 0) parts.push(`${tests.ok} tests passed`);
  if (highlighted) parts.push("selected by this dbt job");
  return parts.join(", ");
}

export interface ModelGraphProps {
  graph: TransformationGraph;
  /**
   * The nodes ONE transformation selected (its `selectedBy`), outlined as
   * "this transformation". Absent when that is not known: nothing is outlined
   * on a guess.
   */
  highlight?: ReadonlySet<string>;
}

/** A node's facts: id, build, status, cause, rows, time, dependencies and tests. */
function NodeDialog({
  node,
  tests,
  namesById,
  onClose,
}: {
  node: TransformationGraphNode;
  tests: TestCounts;
  namesById: ReadonlyMap<string, string>;
  onClose: () => void;
}) {
  return (
    <SkyDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={node.name}
      size="small"
      testId="model-node-dialog"
      secondaryAction={{ label: "Close", onClick: onClose }}
    >
      <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-2">
        <dt>
          <SkyText size={200} color="muted">
            Node
          </SkyText>
        </dt>
        <dd>
          <SkyText size={200} font="monospace" block className="break-all">
            {node.uniqueId}
          </SkyText>
        </dd>
        <dt>
          <SkyText size={200} color="muted">
            Materialized
          </SkyText>
        </dt>
        <dd>
          <SkyText size={200} block>
            {node.materialized ? materializationLabel(node.materialized) : node.resourceType}
            {node.relationName ? ` · ${node.relationName}` : ""}
          </SkyText>
        </dd>
        <dt>
          <SkyText size={200} color="muted">
            Status
          </SkyText>
        </dt>
        <dd>
          {nodeStatusPresentation(node.status) ? (
            <SkyBadge
              appearance="tint"
              color={(nodeStatusPresentation(node.status) as Presentation).color}
            >
              {(nodeStatusPresentation(node.status) as Presentation).label}
            </SkyBadge>
          ) : (
            <SkyText size={200} color="muted">
              Not run
            </SkyText>
          )}
        </dd>
        {node.cause || node.failed ? (
          <>
            <dt>
              <SkyText size={200} color="muted">
                Cause
              </SkyText>
            </dt>
            <dd>
              {node.cause ? (
                <CauseBlock whyOnlyWhenGeneric cause={node.cause} testId="model-graph-cause" />
              ) : (
                <CauseNotClassified testId="model-graph-cause-not-classified" />
              )}
            </dd>
          </>
        ) : null}
        <dt>
          <SkyText size={200} color="muted">
            Rows
          </SkyText>
        </dt>
        <dd>
          <SkyText size={200} block font="numeric">
            {formatRows(nodeRows(node))}
          </SkyText>
        </dd>
        <dt>
          <SkyText size={200} color="muted">
            Execution
          </SkyText>
        </dt>
        <dd>
          <SkyText size={200} block font="numeric">
            {formatSeconds(node.executionTimeSeconds)}
          </SkyText>
        </dd>
        <dt>
          <SkyText size={200} color="muted">
            Depends on
          </SkyText>
        </dt>
        <dd>
          <SkyText size={200} block>
            {node.dependsOn.length === 0
              ? "—"
              : node.dependsOn.map((id) => namesById.get(id) ?? id).join(", ")}
          </SkyText>
        </dd>
        <dt>
          <SkyText size={200} color="muted">
            Tests
          </SkyText>
        </dt>
        <dd>
          <SkyText size={200} block>
            {testCountsLine(tests)}
          </SkyText>
        </dd>
      </dl>
    </SkyDialog>
  );
}

/** The step an arrow key moves the focus between nodes: forward, back, or none. */
function arrowStep(key: string): number {
  if (key === "ArrowRight" || key === "ArrowDown") return 1;
  if (key === "ArrowLeft" || key === "ArrowUp") return -1;
  return 0;
}

/** A node's frame: a source is muted and dashed, a failed node has the destructive stroke. */
function nodeFrameClass(isSource: boolean, failed: boolean): string {
  if (isSource) return "fill-muted stroke-border";
  return failed ? "fill-background stroke-destructive" : "fill-background stroke-border";
}

export function ModelGraph({ graph, highlight }: ModelGraphProps) {
  const layout = useMemo(() => layoutGraph(graph.nodes, graph.edges), [graph.nodes, graph.edges]);
  const nodeRefs = useRef<Array<SVGGElement | null>>([]);
  const [openNode, setOpenNode] = useState<TransformationGraphNode | null>(null);

  const testsByNode = useMemo(
    () =>
      new Map(
        graph.nodes.map((node) => [node.uniqueId, testCountsFor(graph.tests, node.uniqueId)]),
      ),
    [graph.nodes, graph.tests],
  );
  const namesById = useMemo(
    () => new Map(graph.nodes.map((node) => [node.uniqueId, node.name])),
    [graph.nodes],
  );

  const onNodeKeyDown = (event: KeyboardEvent<SVGGElement>, index: number) => {
    const node = layout.nodes[index]?.node;
    if (!node) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpenNode(node);
      return;
    }
    const step = arrowStep(event.key);
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + layout.nodes.length) % layout.nodes.length;
    nodeRefs.current[next]?.focus();
  };

  const openTests = openNode ? (testsByNode.get(openNode.uniqueId) ?? NO_TEST_COUNTS) : null;

  return (
    <div
      className="rounded-lg border border-border bg-card"
      data-testid="model-graph"
      aria-label={`Model graph: ${layout.nodes.length} nodes, ${layout.edges.length} edges`}
    >
      {/* The one scroller on the page: sideways for depth, and capped in height
          so a project with a hundred models in one column scrolls rather than
          pushing the legend off the screen. */}
      <div className="max-h-[70vh] overflow-auto">
        <svg
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          width={layout.width}
          height={layout.height}
          role="group"
          aria-label="Model graph from the last run"
          className="block font-sans"
          data-testid="model-graph-svg"
        >
          {layout.columns.map((column) => (
            <text
              key={column.depth}
              x={column.x}
              y={ORIGIN_Y - 12}
              className="fill-muted-foreground text-xs uppercase tracking-widest"
            >
              {column.label}
            </text>
          ))}

          {/* Edges first, so the nodes draw over them. */}
          {layout.edges.map((laid) => (
            <path
              key={`${laid.edge.from}->${laid.edge.to}`}
              d={laid.path}
              className="fill-none stroke-input"
              strokeWidth={1.5}
              data-testid="model-graph-edge"
              data-from={laid.edge.from}
              data-to={laid.edge.to}
            />
          ))}

          {layout.nodes.map((laid, index) => {
            const { node } = laid;
            const tests = testsByNode.get(node.uniqueId) ?? NO_TEST_COUNTS;
            const status = nodeStatusPresentation(node.status);
            const isSource = node.resourceType === "source";
            const failed = status?.color === "danger";
            const highlighted = highlight?.has(node.uniqueId) ?? false;
            let pillX = NODE_WIDTH - 8;
            const pills: Array<{ x: number; label: string; color: Presentation["color"] }> = [];
            // #1207 round 1: a pill only for tests with a result. A test that did not run (Run
            // tests off) has no status and is counted as not run, so it never reads "0 passed".
            if (!isSource && tests.ok + tests.failed > 0) {
              if (tests.failed > 0) {
                const label = `${tests.failed} failed`;
                pillX -= pillWidth(label);
                pills.push({ x: pillX, label, color: "danger" });
                pillX -= 4;
              }
              // #1207: the count is the tests that passed, so it says "passed".
              const label = `${tests.ok} passed`;
              pillX -= pillWidth(label);
              pills.push({ x: pillX, label, color: tests.failed > 0 ? "subtle" : "success" });
            }
            return (
              <g
                key={node.uniqueId}
                ref={(element) => {
                  nodeRefs.current[index] = element;
                }}
                transform={`translate(${laid.x},${laid.y})`}
                tabIndex={0}
                role="button"
                aria-haspopup="dialog"
                aria-expanded={openNode?.uniqueId === node.uniqueId}
                aria-label={nodeAriaLabel(node, tests, highlighted)}
                className="cursor-pointer outline-none focus-visible:[&>rect:first-child]:stroke-ring"
                data-testid={`model-node-${node.name}`}
                data-status={node.status ?? "none"}
                data-selected={highlighted ? "true" : undefined}
                onClick={() => setOpenNode(node)}
                onKeyDown={(event) => onNodeKeyDown(event, index)}
              >
                <rect
                  width={NODE_WIDTH}
                  height={NODE_HEIGHT}
                  rx={6}
                  className={nodeFrameClass(isSource, failed)}
                  strokeWidth={failed ? 2 : 1}
                  strokeDasharray={isSource ? "4 3" : undefined}
                />
                {highlighted ? (
                  // Drawn after the node's own frame so the focus ring (the
                  // first rect) and the failed stroke stay as they are.
                  <rect
                    x={-4}
                    y={-4}
                    width={NODE_WIDTH + 8}
                    height={NODE_HEIGHT + 8}
                    rx={9}
                    className="fill-none stroke-info"
                    strokeWidth={2}
                    data-testid={`model-node-outline-${node.name}`}
                  />
                ) : null}
                <text
                  x={10}
                  y={16}
                  className="fill-muted-foreground text-xs uppercase tracking-wider"
                >
                  {isSource ? "source" : (node.materialized ?? node.resourceType)}
                </text>
                <text x={10} y={35} className="fill-foreground text-xs font-semibold">
                  {node.name}
                </text>
                <text x={10} y={54} className="fill-muted-foreground text-xs tabular-nums">
                  {nodeMeasure(node)}
                </text>
                {pills.map((pill) => (
                  <Pill key={pill.label} x={pill.x} y={41} label={pill.label} color={pill.color} />
                ))}
                {status ? (
                  <Pill
                    x={NODE_WIDTH - 8 - pillWidth(status.label)}
                    y={8}
                    label={status.label}
                    color={status.color}
                  />
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>

      <div
        className="flex flex-wrap gap-4 border-t border-border px-3 py-2"
        data-testid="model-graph-legend"
      >
        <SkyText size={200} color="muted">
          <span className="mr-2 inline-block h-2.5 w-3.5 rounded-sm border border-border bg-background align-middle" />
          <span>Model</span>
        </SkyText>
        <SkyText size={200} color="muted">
          <span className="mr-2 inline-block h-2.5 w-3.5 rounded-sm border border-dashed border-border bg-muted align-middle" />
          <span>Source, not run</span>
        </SkyText>
        <SkyText size={200} color="muted">
          <SkyBadge appearance="tint" color="success">
            Succeeded
          </SkyBadge>{" "}
          Result in the last run
        </SkyText>
        {/* #1207: the tests key only for a run that has test results. `graph.tests` lists the
            project's tests whether or not they ran, and a test that did not run has no status. */}
        {graph.tests.some((test) => test.status !== null) ? (
          <SkyText size={200} color="muted" testId="model-graph-legend-tests">
            <SkyBadge appearance="tint" color="success">
              2 passed
            </SkyBadge>{" "}
            <SkyBadge appearance="tint" color="danger">
              1 failed
            </SkyBadge>{" "}
            Tests on the model
          </SkyText>
        ) : null}
        {highlight ? (
          <SkyText size={200} color="muted" testId="model-graph-legend-selected">
            <span className="mr-2 inline-block h-2.5 w-3.5 rounded-sm border-2 border-info align-middle" />
            This dbt job ({highlight.size} node{highlight.size === 1 ? "" : "s"})
          </SkyText>
        ) : null}
        <SkyText size={200} color="muted">
          Lines run from a model to the models built from it.
        </SkyText>
      </div>

      {/* Mounted only while a node is open, so its facts are always that node's. */}
      {openNode && openTests ? (
        <NodeDialog
          node={openNode}
          tests={openTests}
          namesById={namesById}
          onClose={() => setOpenNode(null)}
        />
      ) : null}
    </div>
  );
}
